import { useState } from 'react';
import { MapPin, Check, X, Navigation } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../contexts/AuthContext';
import { usePincode } from '../contexts/PincodeContext';
import { t } from '../lib/translations';
import { toast } from 'sonner';

export const PincodeModal = ({ open, onClose }) => {
  const { language } = useAuth();
  const { currentPincode, checkPincode, loading } = usePincode();
  const [pincode, setPincode] = useState(currentPincode);
  const [result, setResult] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);

  const handleCheck = async () => {
    if (pincode.length !== 6) return;
    const res = await checkPincode(pincode);
    setResult(res);
    if (res.available) {
      setTimeout(() => {
        onClose();
        setResult(null);
      }, 1500);
    }
  };

  const handleGetLocation = async () => {
    if (!navigator.geolocation) {
      toast.error(language === 'hi' ? 'लोकेशन सपोर्ट नहीं है' : 'Location not supported');
      return;
    }

    setGettingLocation(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          // Use reverse geocoding API to get pincode
          const response = await fetch(
            `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
          );
          const data = await response.json();
          if (data.postcode) {
            setPincode(data.postcode);
            toast.success(language === 'hi' ? 'पिनकोड मिल गया!' : 'Pincode found!');
          } else {
            toast.error(language === 'hi' ? 'पिनकोड नहीं मिला' : 'Pincode not found');
          }
        } catch (error) {
          toast.error(language === 'hi' ? 'लोकेशन से पिनकोड नहीं मिल सका' : 'Could not get pincode from location');
        } finally {
          setGettingLocation(false);
        }
      },
      (error) => {
        toast.error(language === 'hi' ? 'लोकेशन एक्सेस अस्वीकृत' : 'Location access denied');
        setGettingLocation(false);
      }
    );
  };

  const handleClose = () => {
    onClose();
    setResult(null);
    setPincode(currentPincode);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="pincode-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-green-600" />
            {t('enterPincode', language)}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 pt-4">
          <div className="flex gap-2">
            <Input
              type="text"
              maxLength={6}
              placeholder="247776"
              value={pincode}
              onChange={(e) => {
                setPincode(e.target.value.replace(/\D/g, ''));
                setResult(null);
              }}
              className="text-lg tracking-widest"
              data-testid="pincode-input"
            />
            <Button
              onClick={handleCheck}
              disabled={pincode.length !== 6 || loading}
              className="bg-green-600 hover:bg-green-700"
              data-testid="check-pincode-btn"
            >
              {t('checkDelivery', language)}
            </Button>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex-1 h-px bg-slate-200"></div>
            <span className="text-xs text-slate-500">
              {language === 'hi' ? 'या' : 'or'}
            </span>
            <div className="flex-1 h-px bg-slate-200"></div>
          </div>

          <Button
            onClick={handleGetLocation}
            disabled={gettingLocation}
            variant="outline"
            className="w-full"
          >
            <Navigation className="w-4 h-4 mr-2" />
            {gettingLocation
              ? (language === 'hi' ? 'लोकेशन प्राप्त कर रहे हैं...' : 'Getting location...')
              : (language === 'hi' ? 'मेरा लोकेशन उपयोग करें' : 'Use My Location')}
          </Button>

          {result && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg ${
                result.available ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
              }`}
              data-testid="pincode-result"
            >
              {result.available ? (
                <>
                  <Check className="w-5 h-5" />
                  <span>
                    {t('deliveryAvailable', language)} - {result.area}
                  </span>
                </>
              ) : (
                <>
                  <X className="w-5 h-5" />
                  <div>
                    <p className="font-medium">{t('deliveryNotAvailable', language)}</p>
                    <p className="text-sm mt-1">
                      {language === 'hi'
                        ? 'हम विस्तार कर रहे हैं! जल्द ही हम आपके क्षेत्र में पहुंचेंगे।'
                        : 'We are expanding! We will reach your location soon.'}
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          <p className="text-sm text-slate-500">
            {language === 'hi'
              ? 'वर्तमान में हम केवल पिनकोड 247776 (शामली) में डिलीवरी करते हैं।'
              : 'Currently we deliver only to pincode 247776 (Shamli).'}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PincodeModal;
